import type {AnalyticsInstance, AnalyticsPlugin} from 'analytics';

import {
  CLICK_TAXONOMY_ATTRS,
  ClickAction,
  ClickCategory,
  VALID_CATEGORY_ACTION_PAIRS,
} from '../clickTaxonomy';

const KNOWN_CATEGORIES = new Set<string>(Object.values(ClickCategory));
const KNOWN_ACTIONS = new Set<string>(Object.values(ClickAction));
const TAXONOMY_ANCESTOR_DEPTH_LIMIT = 3;

const INTERACTIVE_SELECTOR =
  'a[href], button, input[type="button"], input[type="submit"], input[type="reset"], [role="button"]';

const MAX_TEXT_LENGTH = 255;

function truncate(s: string): string {
  return s.length > MAX_TEXT_LENGTH ? s.slice(0, MAX_TEXT_LENGTH) : s;
}

function getElementType(el: Element): 'button' | 'link' | 'submit' | 'reset' {
  // For <button> elements, use the explicit type attribute.
  // getAttribute('type') returns null for bare <button> (default type is "submit" in HTML,
  // but we treat unset as "button" so bare <button> does not report as a form submit).
  if (el instanceof HTMLButtonElement) {
    const explicitType = el.getAttribute('type');
    if (explicitType === 'submit') return 'submit';
    if (explicitType === 'reset') return 'reset';
    return 'button';
  }
  // For <input> elements, the type attribute is always explicit.
  if (el instanceof HTMLInputElement) {
    if (el.type === 'submit') return 'submit';
    if (el.type === 'reset') return 'reset';
    return 'button'; // input[type=button]
  }
  // [role="button"] on non-button elements (div, span, etc.)
  if (el.getAttribute('role') === 'button') return 'button';
  // a[href]
  return 'link';
}

// innerText is empty for detached elements in jsdom (no layout engine); fall back to textContent.
function getVisibleText(el: Element): string {
  return ((el as HTMLElement).innerText || el.textContent || '').trim();
}

function getElementName(el: Element): string | undefined {
  const dataName = el.getAttribute('data-element-name');
  if (dataName) return truncate(dataName);

  if (el instanceof HTMLInputElement) {
    return el.value ? truncate(el.value) : undefined;
  }

  const text = getVisibleText(el);
  if (text) return truncate(text);

  const ariaLabel = el.getAttribute('aria-label');
  return ariaLabel ? truncate(ariaLabel) : undefined;
}

function getText(el: Element): string {
  if (el instanceof HTMLInputElement) return truncate(el.value);
  return truncate(getVisibleText(el));
}

function getHref(el: Element): string | undefined {
  if (el instanceof HTMLAnchorElement) {
    return el.href || undefined;
  }
  const anchor = el.closest('a[href]') as HTMLAnchorElement | null;
  return anchor?.href || undefined;
}

export function findTaxonomyAncestor(el: Element): Element | null {
  // Self: direct usage — taxonomy attrs placed on the interactive element itself.
  if (
    el.hasAttribute(CLICK_TAXONOMY_ATTRS.CATEGORY) ||
    el.hasAttribute(CLICK_TAXONOMY_ATTRS.ACTION) ||
    el.hasAttribute(CLICK_TAXONOMY_ATTRS.CONTEXT)
  ) {
    return el;
  }
  // Ancestors: look for the ClickTaxonomyBoundary sentinel within the depth limit.
  let node = el.parentElement;
  for (let depth = 1; depth <= TAXONOMY_ANCESTOR_DEPTH_LIMIT; depth++) {
    if (!node) return null;
    if (node.hasAttribute(CLICK_TAXONOMY_ATTRS.BOUNDARY)) return node;
    if (node === document.body) return null;
    node = node.parentElement;
  }
  return null;
}

export function safeParseContext(raw: string | null): string | undefined {
  if (!raw) return undefined;
  try {
    return JSON.stringify(JSON.parse(raw));
  } catch {
    return undefined;
  }
}

export function extractTaxonomyProperties(el: Element): Partial<{
  click_category: ClickCategory;
  click_action: ClickAction;
  click_context: string;
}> {
  const anchor = findTaxonomyAncestor(el);
  if (!anchor) return {};

  const rawCategory = anchor.getAttribute(CLICK_TAXONOMY_ATTRS.CATEGORY);
  const rawAction = anchor.getAttribute(CLICK_TAXONOMY_ATTRS.ACTION);

  const result: Partial<{
    click_category: ClickCategory;
    click_action: ClickAction;
    click_context: string;
  }> = {};

  if (rawCategory !== null) {
    if (KNOWN_CATEGORIES.has(rawCategory)) {
      result.click_category = rawCategory as ClickCategory;
    } else {
      console.warn(`[analytics] Unknown click_category: "${rawCategory}"`);
    }
  }

  if (rawAction !== null) {
    if (KNOWN_ACTIONS.has(rawAction)) {
      result.click_action = rawAction as ClickAction;
    } else {
      console.warn(`[analytics] Unknown click_action: "${rawAction}"`);
    }
  }

  if (result.click_category && result.click_action) {
    const pair = `${result.click_category}:${result.click_action}`;
    if (!VALID_CATEGORY_ACTION_PAIRS.has(pair)) {
      console.warn(`[analytics] Invalid category/action pair: "${pair}"`);
    }
  }

  const context = safeParseContext(anchor.getAttribute(CLICK_TAXONOMY_ATTRS.CONTEXT));
  if (context !== undefined) result.click_context = context;

  return result;
}

export interface ClickProperties {
  element_type: 'button' | 'link' | 'submit' | 'reset';
  element_html_type: string;
  text: string;
  element_name?: string;
  element_id?: string;
  href?: string;
  click_category?: ClickCategory;
  click_action?: ClickAction;
  click_context?: string;
}

export function extractProperties(el: Element): ClickProperties {
  const elementName = getElementName(el);
  const href = getHref(el);

  return {
    element_type: getElementType(el),
    element_html_type: el.tagName.toLowerCase(),
    text: getText(el),
    ...(elementName !== undefined && {element_name: elementName}),
    ...(el.id && {element_id: el.id}),
    ...(href && {href}),
    ...extractTaxonomyProperties(el),
  };
}

export default function clickTracker(): AnalyticsPlugin {
  let attached = false;

  return {
    name: 'clickTracker',
    initialize: ({instance}: {instance: AnalyticsInstance}) => {
      if (typeof document === 'undefined') return;
      if (attached) return;
      attached = true;

      document.addEventListener(
        'click',
        (event: MouseEvent) => {
          const target = event.target as Element | null;
          if (!target) return;
          const el = target.closest(INTERACTIVE_SELECTOR);
          if (!el) return;
          void instance.track('element:clicked', extractProperties(el));
        },
        {capture: true},
      );
    },
  } as AnalyticsPlugin;
}
